import { useMediaQuery } from '../../hooks/useMediaQuery';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { type Appointment, type Doctor, type Patient } from './types';
import { getFallbackColor, UNASSIGNED_DOCTOR_COLOR } from './utils';
import { notifyDragStart, notifyDragEnd, shouldSuppressClick } from './dragClickGuard';
import { useTheme } from '../../theme/ThemeContext';

const appointmentBlockStyle: React.CSSProperties = {
  background: '#667eea', color: 'white', padding: '2px 4px', margin: '0', borderRadius: '4px', fontSize: '11px',
  minHeight: '0', minWidth: '60px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', cursor: 'pointer', overflow: 'hidden',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box',
};

interface Props {
  appointments: Appointment[];
  patients: Patient[];
  doctors: Doctor[];
  onAppointmentClick: (a: Appointment, e: React.MouseEvent) => void;
  onMoveAppointment?: (id: number, newStart: Date | null, newEnd: Date | null, newRoom?: number) => void;
}

export const StandByArea: React.FC<Props> = ({ appointments, patients, doctors, onAppointmentClick, onMoveAppointment }) => {
  const { t } = useTranslation();
  const coarsePointer = useMediaQuery('(pointer: coarse)');
  const { tokens } = useTheme();
  const handleDragStart = (e: React.DragEvent, payload: { type: 'appointment'; id: number; durationMs: number }) => {
    notifyDragStart();
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    let data: { type: 'appointment' | 'event'; id: number; durationMs: number } | null = null;
    try { data = JSON.parse(raw); } catch { return; }
    if (!data || data.type !== 'appointment') return;
    
    if (onMoveAppointment) {
      onMoveAppointment(data.id, null, null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  return (
    <div 
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ 
        height: '150px', 
        background: tokens.calendar.grid, 
        borderTop: `2px dashed ${tokens.calendar.divider}`, 
        padding: 8, 
        overflowY: 'auto', 
        boxSizing: 'border-box',
        flexShrink: 0
      }}
    >
      <div style={{ fontSize: 11, color: tokens.calendar.muted, marginBottom: 8, textAlign: 'center', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('calendar.standBy')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {appointments.map(app => {
          const patient = patients.find(p => p.id === app.patientId);
          const doc = app.doctorId ? doctors.find(d => d.id === app.doctorId) : undefined;
          const linkedTreatmentNames = Array.isArray(app.treatments)
            ? app.treatments.map(treatment => treatment?.name).filter(Boolean)
            : [];
          const reasonLabel = linkedTreatmentNames.length > 0
            ? linkedTreatmentNames.join(', ')
            : app.treatment?.name || app.treatmentTemplate?.name || app.customReason || '';
          const hasAssignedDoctor = !!doc;
          const blockColor = (doc?.color as string | undefined)
            || (hasAssignedDoctor ? app.color : undefined)
            || (!app.doctorId ? UNASSIGNED_DOCTOR_COLOR : undefined)
            || getFallbackColor(app.patientId || 0);
          
          // Default duration if not set (e.g. 15 mins) for dragging back to calendar
          const durationMs = (app.duration || 15) * 60 * 1000; 

          return (
            <div 
              key={app.id}
              onClick={(e) => {
                if (shouldSuppressClick()) return;
                onAppointmentClick(app, e);
              }}
              draggable={!coarsePointer}
              onDragStart={(e) => handleDragStart(e, { type: 'appointment', id: app.id, durationMs })}
              onDragEnd={() => notifyDragEnd()}
              style={{
                ...appointmentBlockStyle,
                background: blockColor,
                position: 'relative',
                minHeight: 'auto',
                padding: '6px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '140px'
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12 }}>
                {patient?.name || t('calendar.unknownPatient')}
                {patient?.hc && ` - ${patient.hc}`}
              </div>
              {reasonLabel && <div style={{ fontSize: 11, opacity: 0.9 }}>{reasonLabel}</div>}
              <div style={{ fontSize: 10, opacity: 0.75 }}>
                {doc ? `${t('calendar.dr')} ${doc.lastName}` : t('calendar.unassignedDoctor', 'No doctor assigned')}
              </div>
              <div style={{ fontSize: 10, opacity: 0.75 }}>{app.duration || 15} {t('calendar.min')}</div>
            </div>
          );
        })}
        {appointments.length === 0 && (
          <div style={{ fontSize: 11, color: tokens.calendar.muted, textAlign: 'center', padding: '12px 0', width: '100%' }}>
            {t('calendar.noStandBy')}
          </div>
        )}
      </div>
    </div>
  );
};
